import React, { useCallback, useState, useEffect } from 'react';
import { Card, Form, InputGroup } from 'react-bootstrap-5';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faMinusCircle, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { get } from 'helpers/api';
import { debounce } from 'lodash';
import qs from 'qs';

interface UserListProps {
  companyId?: number;
  includeUserIds?: number[];
  excludeUserIds?: number[];
  onAddUser?: (id: number) => void;
  onRemoveUser?: (id: number) => void;
}

interface User {
  id: number;
  name: string;
}

export default function UserList({
  companyId,
  includeUserIds,
  excludeUserIds,
  onAddUser,
  onRemoveUser
}: UserListProps) {
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);

  const searchUsers = useCallback(
    debounce((_searchText: string, _companyId?: number, _includeUserIds?: number[], _excludeUserIds?: number[]) => {
      const params: { limit: number, [key: string]: any } = { limit: 15 };
      if (_searchText) params['q[first_name_or_last_name_or_login_credential_email_cont]'] = _searchText;
      if (_companyId) params['company_id'] = _companyId;
      let queryString = qs.stringify(params);

      if (_includeUserIds) _includeUserIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_excludeUserIds) _excludeUserIds.forEach((id: number) => {
        queryString += `&q[id_not_in][]=${id}`;
      });

      get(`/users/search?${queryString}`).then((data: { users: User[] }) => {
        setUsers(data.users);
        setLoading(false);
      });
    }, 400),
    []
  );

  useEffect(() => {
    setLoading(true);

    if (onRemoveUser && !includeUserIds?.length) {
      setUsers([]);
      setLoading(false);
    } else {
      searchUsers(searchText, companyId, includeUserIds, excludeUserIds);
    }
  }, [searchText, companyId, includeUserIds, excludeUserIds, searchUsers, onRemoveUser]);

  const emptyText = () => {
    if (loading) return 'Loading...';

    if (onRemoveUser && includeUserIds?.length === 0) return 'Start by adding users';

    return 'No Users Found';
  };

  return (
    <div>
      <Form.Group className="mb-3">
        <InputGroup>
          <InputGroup.Text className="text-primary">
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search"
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <button
            aria-label="Clear search"
            className="btn btn-outline-secondary"
            disabled={!searchText}
            type="button"
            onClick={() => setSearchText('')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </InputGroup>
      </Form.Group>
      <Card className="overflow-auto" style={{ height: 300 }}>
        {users.length > 0 ? (
          <ul className="list-group list-group-flush">
            {users.map(({ name, id }) => (
              <li className="list-group-item d-flex align-items-center" key={id}>
                <span className="flex-grow-1">{name}</span>
                {onAddUser && (
                  <span className="ms-auto pe-2 d-inline-flex align-items-center">
                    {loading ? (
                      <span aria-label="Loading" className="spinner-border" role="status" />
                    ) : (
                      <FontAwesomeIcon
                        className="text-success clickable"
                        icon={faPlusCircle}
                        name="add-user"
                        onClick={() => onAddUser(id)}
                      />
                    )}
                  </span>
                )}
                {onRemoveUser && (
                  <span className="ms-auto pe-2 d-inline-flex align-items-center">
                    {loading ? (
                      <span aria-label="Loading" className="spinner-border" role="status" />
                    ) : (
                      <FontAwesomeIcon
                        className="text-danger clickable"
                        icon={faMinusCircle}
                        name="remove-user"
                        onClick={() => onRemoveUser(id)}
                      />
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="d-flex h-100 align-items-center justify-content-center">
            <h5 className="text-muted text-center m-0">
              {emptyText()}
            </h5>
          </div>
        )}
      </Card>
    </div>
  );
}
